import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/brand";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import Home from "@/pages/marketing/Home";
import Pricing from "@/pages/marketing/Pricing";
import CmsPage from "@/pages/marketing/CmsPage";
import Contact from "@/pages/marketing/Contact";
import { Login, Register, ForgotPassword, ResetPassword, AcceptInvite, VerifyEmail } from "@/pages/auth/AuthPages";
import Onboarding from "@/pages/app/Onboarding";
import AppLayout from "@/components/app/AppLayout";
import Dashboard from "@/pages/app/Dashboard";
import Generate from "@/pages/app/Generate";
import DocumentView from "@/pages/app/DocumentView";
import Library from "@/pages/app/Library";
import Billing from "@/pages/app/Billing";
import Settings from "@/pages/app/Settings";
import Team from "@/pages/app/Team";
import Training from "@/pages/app/Training";
import CourseEditor from "@/pages/app/CourseEditor";
import TakeCourse from "@/pages/app/TakeCourse";
import Permits from "@/pages/app/Permits";
import PermitBoard, { PublicPermitBoard } from "@/pages/app/PermitBoard";
import PublicEmergency from "@/pages/PublicEmergency";
import Incidents from "@/pages/app/Incidents";
import ReportIncident from "@/pages/app/ReportIncident";
import IncidentView from "@/pages/app/IncidentView";
import Projects from "@/pages/app/Projects";
import KnowledgeBase from "@/pages/app/KnowledgeBase";
import ProjectView from "@/pages/app/ProjectView";
import Inspections from "@/pages/app/Inspections";
import InspectionPage from "@/pages/app/InspectionPage";
import PermitView from "@/pages/app/PermitView";
import NotificationSettings from "@/pages/app/NotificationSettings";
import AdminLayout from "@/pages/admin/AdminLayout";
import { AdminDashboard, AdminCompanies, AdminActivity } from "@/pages/admin/AdminPages";
import { AdminPlans, AdminCategories, AdminCms, AdminSettings } from "@/pages/admin/AdminConfig";

const OnboardingGate = () => {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.org_id) return <Navigate to="/app" replace />;
  if (user.is_super_admin) return <Navigate to="/admin" replace />;
  return <Onboarding />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route element={<MarketingLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/p/:slug" element={<CmsPage />} />
            <Route path="/legal/:slug" element={<CmsPage />} />
          </Route>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/board/:token" element={<PublicPermitBoard />} />
          <Route path="/e/:token" element={<PublicEmergency />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/onboarding" element={<OnboardingGate />} />
          <Route path="/app" element={<AppLayout />}>
            <Route index element={<Dashboard />} />
            <Route path="generate" element={<Generate />} />
            <Route path="documents" element={<Library />} />
            <Route path="documents/:id" element={<DocumentView />} />
            <Route path="team" element={<Team />} />
            <Route path="training" element={<Training />} />
            <Route path="training/courses/:id" element={<CourseEditor />} />
            <Route path="training/take/:id" element={<TakeCourse />} />
            <Route path="permits" element={<Permits />} />
            <Route path="permits/board" element={<PermitBoard />} />
            <Route path="permits/:id" element={<PermitView />} />
            <Route path="incidents" element={<Incidents />} />
            <Route path="incidents/new" element={<ReportIncident />} />
            <Route path="incidents/:id" element={<IncidentView />} />
            <Route path="projects" element={<Projects />} />
            <Route path="projects/:id" element={<ProjectView />} />
            <Route path="knowledge" element={<KnowledgeBase />} />
            <Route path="inspections" element={<Inspections />} />
            <Route path="inspections/:id" element={<InspectionPage />} />
            <Route path="notification-settings" element={<NotificationSettings />} />
            <Route path="billing" element={<Billing />} />
            <Route path="settings" element={<Settings />} />
          </Route>
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="companies" element={<AdminCompanies />} />
            <Route path="plans" element={<AdminPlans />} />
            <Route path="categories" element={<AdminCategories />} />
            <Route path="cms" element={<AdminCms />} />
            <Route path="activity" element={<AdminActivity />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </AuthProvider>
  );
}

export default App;
